/**
 * hullwalk.mjs — the walk for the other seven, derived from what each one declares.
 *
 * FOUNDER, 2026-08-20: "now do the walkthrough for the other ships too".
 *
 * ── THE DP WALK CANNOT SIMPLY BE POINTED AT THEM, AND THAT IS THE FINDING ─────────────────────
 * Measured before building: of the eight ships in the fleet, exactly ONE declares a DP system.
 * The DP walk goes switchboard → generating sets → thrusters → the bulkhead between the halves.
 * The other seven have no switchboards, no DP thrusters and no redundancy boundary, so running
 * that walk over them would mean inventing a redundancy chain none of them has — the fabrication
 * this whole estate is built against, dressed as a feature request being honoured.
 *
 * ── SO THE WALK IS THE ONE THEIR OWN CHAPTER IS ABOUT ─────────────────────────────────────────
 * NavalArch101 chapter 1 is "A Ship Is Eight Ships", and its closing line is the lab: "A learner
 * who meets one ship learns a ship. A learner who meets eight learns that the type was the
 * question." Measured across the fleet, that claim is literally true of the geometry:
 *
 *     seven of the eight are 176 m long, and NINE parts are common to all eight
 *     everything else is type — hoppers and topsides on the bulker, an inner hull and longitudinal
 *     bulkheads on the tanker, a torsion box and cell guides on the container feeder, vehicle decks
 *     and a stern ramp on the ro-ro, four decks and fire zones on the passenger ship
 *
 * So the walk climbs each ship keel-up through HER OWN declared layers, and names what is hers
 * alone. Same length, different ship, computed rather than asserted.
 *
 * ── THE STATIONS ARE DERIVED FROM HER LAYERS, NEVER LISTED ────────────────────────────────────
 * Each model declares an ordered `layers` array and each part declares its `layer`. A hand-written
 * station list per arrangement would be eight hand-kept maps — the fault this session has closed
 * four times already. A ship that gains a layer gains a station with nobody editing this file, and
 * a layer that declares no parts is dropped rather than pointing a camera at nothing.
 */

/** Parts every ship in the fleet declares — the hull that is a hull whatever she carries. */
export function sharedParts(models) {
  const sets = models.map((m) => new Set((m.parts || []).map((p) => p.id)));
  if (!sets.length) return new Set();
  return sets.reduce((a, b) => new Set([...a].filter((x) => b.has(x))));
}

/** What this one has that the common hull does not — her type, in parts. */
export const distinctiveParts = (model, shared) =>
  (model.parts || []).filter((p) => !shared.has(p.id));

/**
 * Keel-up stations from her own declared layers.
 *
 * `layers` arrives ordered from the shell outward/upward, which is the order she is built in and
 * the order NavalArch101 chapter 8 walks. It is used as declared rather than re-sorted here: a
 * re-sort would be this file having an opinion about her arrangement, which is hers to state.
 */
export function hullStations(model, shared = null) {
  const parts = model.parts || [];
  const byLayer = new Map();
  for (const p of parts) {
    if (!byLayer.has(p.layer)) byLayer.set(p.layer, []);
    byLayer.get(p.layer).push(p);
  }
  const out = [{ id: 'whole', name: 'The whole ship', focus: null, layer: null,
    says: `${model.vessel} — ${model.type}. ${parts.length} declared parts, compiled from her table of offsets and her scantlings.` }];

  for (const layer of model.layers || []) {
    const ps = byLayer.get(layer) || [];
    if (!ps.length) continue;                       // a declared layer with no geometry is skipped, not faked
    const own = shared ? ps.filter((p) => !shared.has(p.id)) : [];
    out.push({
      id: `layer:${layer}`,
      name: layer.replace(/_/g, ' '),
      layer,
      focus: ps.map((p) => p.id),
      distinctive: own.map((p) => p.id),
      says: ps[0].why || ps.map((p) => p.label).join('; '),
    });
  }
  /* Any layer a part claims that her `layers` array does not list — declared by the part and not by
     the ship. Surfaced rather than silently dropped: the two lists disagreeing is a real fault. */
  const listed = new Set(model.layers || []);
  const orphanLayers = [...byLayer.keys()].filter((l) => !listed.has(l));
  return { stations: out, orphanLayers };
}

/** The chapter's claim, computed across the fleet rather than repeated. */
export function fleetShape(models) {
  const shared = sharedParts(models);
  const lengths = models.map((m) => ({ ship: m.ship, vessel: m.vessel, loa_m: +(m.extent.x[1] - m.extent.x[0]).toFixed(1) }));
  const byLength = new Map();
  for (const l of lengths) byLength.set(l.loa_m, (byLength.get(l.loa_m) || 0) + 1);
  const commonest = [...byLength.entries()].sort((a, b) => b[1] - a[1])[0] || [null, 0];
  return {
    shared: [...shared],
    lengths,
    same_length_m: commonest[0],
    same_length_count: commonest[1],
    distinctive: Object.fromEntries(models.map((m) => [m.ship, distinctiveParts(m, shared).map((p) => p.id)])),
  };
}

export const WHY_NOT_THE_DP_WALK =
  'Only one ship in this fleet declares a DP system, so only she has a redundancy boundary to walk. '
  + 'Running the DP walk over the other seven would mean inventing switchboards none of them has. '
  + 'This walk climbs each ship through her own declared layers instead — which is the walk her own '
  + 'chapter is about.';

export const SOURCE_NA1 = 'NavalArch101 chapter 1 — A Ship Is Eight Ships';
export const TYPE_IS_THE_QUESTION = 'A learner who meets one ship learns a ship. A learner who meets eight learns that the type was the question.';
